import { useState, useEffect } from "react";
import { Link, useLocation } from "wouter";
import { Eye, EyeOff } from "lucide-react";
import { Logo } from "../lib/brand";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

export default function Login() {
  const [form, setForm] = useState({ email: "", password: "", rememberMe: false });
  const [error, setError] = useState<string | null>(null);
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [, navigate] = useLocation();
  const { user, setUser } = useAuth();

  // Navigate only after the user is committed to context (avoids a null-user bounce).
  useEffect(() => { if (user) navigate(user.userType === "admin" ? "/admin" : "/dashboard"); }, [user, navigate]);

  // Remember Me: pre-fill the email (never the password) on return.
  useEffect(() => {
    try {
      const saved = localStorage.getItem("kpv3_remember_email");
      if (saved) setForm((f) => ({ ...f, email: saved, rememberMe: true }));
    } catch { /* ignore */ }
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null); setBusy(true);
    try {
      const u = await api.login(form);
      if (u) {
        try {
          if (form.rememberMe) localStorage.setItem("kpv3_remember_email", form.email.trim());
          else localStorage.removeItem("kpv3_remember_email");
        } catch { /* ignore */ }
        setUser(u);
      } else setError("Invalid username or password");
    } catch (err: any) { setError(err?.message || "Invalid username or password"); }
    finally { setBusy(false); }
  }

  return (
    <div className="min-h-[80vh] grid place-items-center container-x py-10">
      <div className="card p-6 sm:p-8 max-w-md w-full">
        <div className="flex justify-center mb-4"><Logo withTagline /></div>
        <h1 className="text-2xl font-extrabold text-navy text-center mb-1">Welcome back</h1>
        <p className="text-sm text-mut text-center mb-6">Sign in to your KontrataPH account</p>
        <form className="space-y-3" onSubmit={submit}>
          <div><label className="label">Email</label><input required type="email" className="field" placeholder="you@example.com" value={form.email} onChange={(e) => { setForm({ ...form, email: e.target.value }); if (error) setError(null); }} /></div>
          <div>
            <label className="label">Password</label>
            <div className="relative">
              <input required type={showPw ? "text" : "password"} className="field pr-11" placeholder="Your password" value={form.password} onChange={(e) => { setForm({ ...form, password: e.target.value }); if (error) setError(null); }} />
              <button type="button" onClick={() => setShowPw((v) => !v)} aria-label={showPw ? "Hide password" : "Show password"} className="absolute inset-y-0 right-0 px-3 flex items-center text-mut hover:text-navy">
                {showPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>
          <div className="flex items-center justify-between text-sm">
            <label className="flex items-center gap-2 text-mut"><input type="checkbox" checked={form.rememberMe} onChange={(e) => setForm({ ...form, rememberMe: e.target.checked })} /> Remember me</label>
            <Link href="/forgot-password" className="text-brand-red font-semibold">Forgot password?</Link>
          </div>
          {error && <p className="text-sm text-brand-red">{error}</p>}
          <button type="submit" disabled={busy} className="btn-red w-full disabled:opacity-60">{busy ? "Signing in…" : "Log In"}</button>
          <p className="text-center text-sm text-mut">Don't have an account? <Link href="/signup" className="text-brand-red font-semibold">Sign Up</Link></p>
        </form>
      </div>
    </div>
  );
}
